import React, { useMemo } from 'react';
import { MessageType } from '../../../../../services/actor-messages';
import { EventBus } from '../../../../contexts/event-bus';
import { WorkspaceManagementContext } from '../../../../contexts/workspace/workspace-management-context';
import { useFlightWorker } from '../../../../hooks/use-flight-worker';
import { useSafeEffect } from '../../../../hooks/use-safe-effect';

export function FlightMapUpdates() {
    const context = React.useContext(WorkspaceManagementContext);
    const worker = useFlightWorker();
    const isMapLoaded = useMemo(() => context.items.every((x) => x.map), [context]);

    useSafeEffect(() => {
        if (!isMapLoaded) {
            return;
        }

        worker.actor.sendAsync({ type: MessageType.startMapUpdates });
        worker.actor.registerMessageHandler(MessageType.initMapFlights, async (data) => {
            for (const itemContext of context.items) {
                if (data.preDeparture) {
                    // this can be called with undefined plannedScheduled field in many cases like tags changed or filters changed
                    // when this field is undefined don't delete any pld schd flights from the map
                    itemContext.flightLayerFacade.scheduled.addData(data.preDeparture);
                }

                if (data.parked) {
                    itemContext.flightLayerFacade.parked.init(data.parked);
                }

                itemContext.flightLayerFacade.deckPlugin.cityPairData = data.cityPair ?? [];
                itemContext.flightLayerFacade.flights.update({ updates: data.active, replace: true });
                itemContext.flightLayerFacade.deckPlugin.updateLayers({ shouldArrangeTags: true });
            }
        });

        worker.actor.registerMessageHandler(MessageType.updateMapFlights, async (data) => {
            // Update flight layer
            for (const itemContext of context.items) {
                const deleted = [...data.active.deleted.all, ...(data.active.deleted.workspaces?.[itemContext.id] ?? [])];
                itemContext.flightLayerFacade.flights.update({ updates: data.active.updated, deleted });

                if (data.preDeparture) {
                    itemContext.flightLayerFacade.scheduled.update(data.preDeparture.updated);
                    itemContext.flightLayerFacade.scheduled.remove(data.preDeparture.deleted);
                }

                if (data.parked) {
                    itemContext.flightLayerFacade.parked.update(data.parked);
                }

                if (data.cityPair) {
                    itemContext.flightLayerFacade.deckPlugin.cityPairData = data.cityPair;
                }

                itemContext.flightLayerFacade.deckPlugin.updateLayers({ shouldArrangeTags: true });
            }
        });

        worker.actor.registerMessageHandler(MessageType.flightRouteFlownPointAdded, async (data) => {
            // TODO: do we patch the redux store? If we do make sure that places that listen to those routes don't waste too much time recomputing things as it's probably only 1 point that changed
            for (const update of data) {
                for (const item of context.items) {
                    item.flightLayerFacade.routes.addFlownRoutePoint(update.flightId, update.point, update.index);
                }
            }
            EventBus.fire('flight-flown-point-added', data);
        });

        return () => {
            worker.actor.sendAsync({ type: MessageType.stopMapUpdates });
            worker.actor.unregisterMessageHandler(MessageType.initMapFlights);
            worker.actor.unregisterMessageHandler(MessageType.updateMapFlights);
            worker.actor.unregisterMessageHandler(MessageType.flightRouteFlownPointAdded);
        };
    }, [isMapLoaded, context, worker]);
    return <></>;
}
